import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import BlackDropdownArrow from '../resources/images/BlackDropdownArrow.svg';
import BodyText from './BodyText';

interface Props {
	className?: any;
	onChange: (value: string) => void;
	label?: string;
	options: string[];
	disabled?: boolean;
	error?: boolean;
	errorText?: string;
	optionChosen: string;
	defaultText: string;
	defaultValue?: string;
	tabIndex?: number;
}

const SelectInput = ({ className, onChange, label, options, disabled, error, errorText, optionChosen, defaultText, defaultValue, tabIndex }: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const [focusedOptionIndex, setFocusedOptionIndex] = useState<number | null>(null);
	const [isKeyboardNavigation, setIsKeyboardNavigation] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

	const selectedOptionIndex = optionChosen !== undefined ? options.indexOf(optionChosen) : defaultValue !== undefined ? options.indexOf(defaultValue) : -1;

	const toggling = () => {
		if (!disabled) {
			setIsOpen(!isOpen);
		}
	};

	const handleClickOutside = (event: MouseEvent) => {
		if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
			setIsOpen(false);
			setFocusedOptionIndex(null);
		}
	};

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		if (focusedOptionIndex !== null && itemRefs.current[focusedOptionIndex]) {
			itemRefs.current[focusedOptionIndex]?.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
			});
		}
	}, [focusedOptionIndex]);

	const handleOptionClick = (value: string) => {
		onChange(value);
		setIsOpen(false);
	};

	const handleArrowKeyNavigation = (event: React.KeyboardEvent<HTMLDivElement>) => {
		event.preventDefault();
		const newIndex =
			focusedOptionIndex === null
				? event.key === 'ArrowDown'
					? 0
					: options.length - 1
				: event.key === 'ArrowDown'
					? (focusedOptionIndex + 1) % options.length
					: (focusedOptionIndex - 1 + options.length) % options.length;
		setFocusedOptionIndex(newIndex);
	};

	const handleEnterKeyAction = () => {
		if (isOpen) {
			if (focusedOptionIndex !== null) {
				handleOptionClick(options[focusedOptionIndex]);
			}
		} else {
			toggling();
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		setIsKeyboardNavigation(true);
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			handleArrowKeyNavigation(event);
		} else if (event.key === 'Enter') {
			handleEnterKeyAction();
			event.preventDefault();
		}
	};

	const handleMouseMove = () => {
		setIsKeyboardNavigation(false);
	};

	return (
		<>
			{label && <StyledLabel className={disabled ? 'disabled' : ''}>{label}</StyledLabel>}
			<StyledContainer className={className}>
				<DropDownHeader
					tabIndex={tabIndex || 0}
					onClick={toggling}
					onKeyPress={toggling}
					onKeyDown={handleKeyDown}
					className={`${className} dropDownElements ${disabled ? 'disabled' : ''}`}
					error={error}
					isOpen={isOpen}
				>
					<StyledSpan isDefaultText={!optionChosen && !defaultValue} isSelected={!!optionChosen}>
						{optionChosen || defaultText}
					</StyledSpan>
					<StyledImg isOpen={isOpen} src={BlackDropdownArrow} className={className} />
				</DropDownHeader>
				{isOpen && (
					<DropDownListContainer ref={dropdownRef} className={'dropDownElements'} id={'dropDownElementsContainer'}>
						<DropDownList onMouseMove={handleMouseMove}>
							{options.map((option: string, index: number) => (
								<ListItem
									key={index}
									onClick={() => handleOptionClick(option)}
									className={'dropDownElements'}
									role="option"
									aria-posinset={index}
									aria-selected={index === selectedOptionIndex}
									isHovered={focusedOptionIndex === index && isKeyboardNavigation}
									onMouseEnter={() => !isKeyboardNavigation && setFocusedOptionIndex(index)}
									ref={(el) => {
										itemRefs.current[index] = el;
									}}
								>
									{option}
								</ListItem>
							))}
						</DropDownList>
					</DropDownListContainer>
				)}
				{error && <ErrorMessage>{errorText}</ErrorMessage>}
			</StyledContainer>
		</>
	);
};

const ErrorMessage = styled(BodyText)`
	font-style: normal;
	font-weight: normal;
	font-size: 13px;
	line-height: 115%;
	color: #e81111;
	text-align: left;
	margin: 8px 0 0px 0;
`;

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	position: relative;
`;

const StyledLabel = styled.label`
	display: block;
	font-family: museo-sans;
	font-style: normal;
	font-weight: normal;
	font-size: 13px;
	line-height: 15px;
	color: #333333;
	padding-bottom: 8px;
	&.disabled {
		opacity: 0.2;
	}
`;

interface HeaderProps {
	error?: boolean;
	isOpen?: boolean;
}

const DropDownHeader = styled.div<HeaderProps>`
	font-size: 16px;
	line-height: 19px;
	color: #333333;
	height: 40px;
	background: #ffffff;
	box-sizing: border-box;
	padding: 11px 16px 10px;
	position: relative;
	border-radius: ${(props) => (props.isOpen ? '4px 4px 0 0 ' : '4px')};
	border: ${(props) => (props.error ? '2px solid #e81111' : '1px solid #919191')};
	&:focus,
	&:active {
		outline: 2px auto #116eee;
	}
	&:disabled {
		opacity: 0.2;
		border: 1px solid #333333;
	}
	&.disabled {
		opacity: 0.2;
	}
`;

const DropDownListContainer = styled('div')`
	position: relative;
	z-index: 100;
	width: 100%;
`;

const DropDownList = styled('ul')`
	margin-top: 0px;
	background: #ffffff;
	border: 1px solid #919191;
	box-sizing: border-box;
	border-radius: 0 0 5px 5px;
	width: 100%;
	border-top: none;
	position: absolute;
	overflow-y: scroll;
	max-height: 156px;
	padding: 7px 19px 7px 5.6px;
	font-size: 16px;
	line-height: 19px;
	-webkit-scrollbar {
		width: 1em;
		padding-left: 7px;
		color: #d7d7d7;
	}
	-webkit-scrollbar-thumb {
		outline: 6px solid #d7d7d7;
		height: 100px;
		color: #d7d7d7;
	}
	-webkit-scrollbar-track {
		box-shadow: inset 0 0 6px #d7d7d7;
	}
`;

interface ListItemProps {
	isHovered: boolean;
}

const ListItem = styled.div<ListItemProps>`
	list-style: none;
	padding: 8px 0 8px 10.4px;
	color: ${(props) => (props.isHovered ? 'white' : '#666666')};
	background: ${(props) => (props.isHovered ? '#116eee' : 'none')};
	border-radius: ${(props) => (props.isHovered ? '5px' : 'none')};
	&:hover {
		background: #116eee;
		border-radius: 5px;
		color: white;
	}
	&:active {
		background: #0b4ca6;
		border-radius: 5px;
		color: white;
	}
	&:focus {
		background: #116eee;
		outline: 2px solid #116eee;
		border-radius: 7px;
		outline-offset: 2px;
		color: white;
	}
`;

interface ImgProps {
	isOpen?: boolean;
}

const StyledImg = styled.img<ImgProps>`
	position: absolute;
	right: 14.29px;
	top: 15px;
	transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'none')};
`;

interface StyledSpanProps {
	isDefaultText: boolean;
	isSelected: boolean;
}

const StyledSpan = styled.span<StyledSpanProps>`
	color: ${(props) => (props.isSelected ? '#333' : '#666666')};
	font-style: ${(props) => (!props.isDefaultText ? 'normal' : 'italic')};
`;

export default SelectInput;
